import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  Search, Plus, FileSpreadsheet, FileText, 
  Settings, Users, Layers, Tag, BarChart2,
  Download, History, Copy, Database, ClipboardList,
  List, Grid3X3, Filter, Calendar, CheckCircle2,
  Clock, AlertCircle, Target, TrendingUp, Save,
  FolderOpen, FileDown, FileUp, HardDrive, Settings2,
  ChevronDown, Filter as FilterIcon, SortAsc, SortDesc,
  RefreshCw, Archive, Trash2, Edit, Eye, EyeOff, Home,
  RotateCcw, RotateCw, Scale, BarChart3, Map, Image
} from 'lucide-react';

interface ActionRibbonProps {
  searchText: string;
  onSearchChange: (text: string) => void;
  viewMode: 'list' | 'kanban' | 'matrix';
  onViewModeChange: (mode: 'list' | 'kanban' | 'matrix') => void;
  onNewAction: () => void;
  onExportExcel?: (type?: 'list' | 'kanban' | 'matrix') => void;
  onGenerateFullReport?: () => void;
  onExportPDF?: (type?: 'list' | 'kanban' | 'matrix' | 'indicators') => void;
  onExportImage?: (type?: 'list' | 'kanban' | 'matrix' | 'indicators') => void;
  onSaveLocal?: () => void;
  onOpenLocal?: () => void;
  onImportExcel?: () => void;
  onDownloadTemplate?: () => void;
  onProjectSettings?: () => void;
  onManageTeam?: () => void;
  onIndicators?: () => void;
  onSave?: () => void;
  onRefresh?: () => void;
  onFilter?: () => void;
  onSort?: () => void;
  goHome?: () => void;
  onUndo?: () => void;
  onRedo?: () => void;
  onCompareScenarios?: () => void;
  onNewMap?: () => void;
  // Novos campos para filtros e sumário
  projects?: Array<{ id: string; nome_projeto: string }>;
  selectedProjectId?: string;
  onProjectChange?: (id: string) => void;
  sectors?: string[];
  selectedSector?: string;
  onSectorChange?: (sector: string) => void;
  responsibles?: string[];
  selectedResponsible?: string;
  onResponsibleChange?: (name: string) => void;
  selectedStatus?: string;
  onStatusChange?: (status: string) => void;
  summary?: { overdue: number; doing: number; done: number };
  // Escopo de visualização das ações
  actionScope?: 'current' | 'all' | 'specific';
  onActionScopeChange?: (scope: 'current' | 'all' | 'specific') => void;
  specificProjectId?: string | null;
  onSpecificProjectChange?: (id: string) => void;
  currentProjectName?: string;
  indicatorsOpen?: boolean;
  // modo de contexto: quando dentro do módulo "Plano de Ação" mostrar toolbar de ações
  mode?: 'action_plan' | 'designer' | 'default';
  // aba ativa do segmento (map | action_plan) para identidade de cor
  activeTab?: 'map' | 'action_plan';
}

// Separador Vertical - Padronizado discreto
const Separator = () => <div className="mx-2 h-5 w-px self-center bg-slate-300/55 dark:bg-slate-700/60" />;

// Botão da Toolbar
interface ToolBtnProps {
  icon: React.ReactNode;
  label?: string;
  onClick?: () => void;
  active?: boolean;
  disabled?: boolean;
  hasDropdown?: boolean;
  className?: string;
  title?: string;
}

// ToolBtn estilo Ribbon: ícone acima, label pequeno abaixo, dimensões de botão de ribbon
const ToolBtn: React.FC<ToolBtnProps> = ({ icon, label, onClick, active, disabled, hasDropdown, className, title }) => {
  return (
    <button
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={`
        group relative flex flex-col items-center justify-center gap-[3px] select-none h-[42px] rounded-sm transition-colors
        ${label ? 'w-[58px] px-1' : 'w-[42px] px-0'}
        ${disabled ? 'opacity-40 cursor-not-allowed' : 'hover:bg-[var(--ribbon-button-hover)] active:bg-[var(--ribbon-button-active)]'}
        ${active ? 'bg-[var(--ribbon-button-active)] border-b-2 border-b-[var(--nav-active-bar)] text-blue-600 dark:text-blue-400' : 'text-[var(--ribbon-text-secondary)] border-b-2 border-b-transparent'}
        ${className || ''}
      `}
      title={title || label}
    >
      {hasDropdown && (
        <svg
          width="7"
          height="5"
          viewBox="0 0 7 5"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="pointer-events-none absolute right-[3px] top-[3px] opacity-50 text-slate-500 dark:text-slate-400"
          aria-hidden="true"
        >
          <path d="M1 1L3.5 4L6 1" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
      <div className={`flex items-center justify-center [&_svg]:h-[16px] [&_svg]:w-[16px] ${active ? 'text-blue-600 dark:text-blue-400' : 'text-[var(--ribbon-text-secondary)]'}`}>
        {icon}
      </div>
      {label && (
        <span className={`text-[10.5px] font-medium leading-tight whitespace-normal text-center line-clamp-2 ${active ? 'text-blue-600 dark:text-blue-400 font-semibold' : 'text-[var(--ribbon-text-secondary)]'}`}>
          {label}
        </span>
      )}
    </button>
  );
};

// ButtonGroup cria ilhas visuais para agrupar ferramentas relacionadas
const ButtonGroup: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <div className={`flex items-center gap-0.5 h-full ${className || ''}`}>
    {children}
  </div>
);

// Segmented navigation converted to Clean Tabs (text-only)
const SegmentedNav: React.FC<{ active: 'map' | 'action_plan'; onSelect?: (tab: 'map' | 'action_plan') => void }> = ({ active, onSelect }) => {
  return (
    <div className="flex items-center gap-4">
      <button
        onClick={() => onSelect?.('map')}
        className={
          `text-sm ${active === 'map' ? 'text-emerald-600 font-semibold border-b-2 border-emerald-500' : 'text-slate-500'} pb-2`
        }
      >
        Mapa VSM
      </button>
      <button
        onClick={() => onSelect?.('action_plan')}
        className={
          `text-sm ${active === 'action_plan' ? 'text-blue-600 font-semibold border-b-2 border-blue-600' : 'text-slate-500'} pb-2`
        }
      >
        Plano de Ação
      </button>
    </div>
  );
};

// Atualizações no Ribbon para hierarquia visual e agrupamento
export const ActionRibbon: React.FC<ActionRibbonProps> = ({
  searchText,
  onSearchChange,
  viewMode,
  onViewModeChange,
  onNewAction,
  onExportExcel,
  onGenerateFullReport,
  onExportPDF,
  onExportImage,
  onSaveLocal,
  onOpenLocal,
  onImportExcel,
  onDownloadTemplate,
  onProjectSettings,
  onManageTeam,
  onIndicators,
  onSave,
  onRefresh,
  onFilter,
  onSort,
  goHome,
  onUndo,
  onRedo,
  onCompareScenarios,
  onNewMap,
  projects = [],
  selectedProjectId = 'all',
  onProjectChange,
  sectors = [],
  selectedSector = 'all',
  onSectorChange,
  responsibles = [],
  selectedResponsible = 'all',
  onResponsibleChange,
  onStatusChange,
  selectedStatus = 'all',
  summary = { overdue: 0, doing: 0, done: 0 },
  mode = 'default',
  activeTab = 'action_plan',
  actionScope = 'current',
  onActionScopeChange,
  specificProjectId = null,
  onSpecificProjectChange,
  currentProjectName = 'Projeto Atual',
  indicatorsOpen = false
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const ribbonRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  // Refs para os botões com dropdown
  const fileButtonRef = useRef<HTMLDivElement>(null);
  const filterButtonRef = useRef<HTMLDivElement>(null);
  const exportPDFButtonRef = useRef<HTMLButtonElement>(null);
  const exportPNGButtonRef = useRef<HTMLButtonElement>(null);

  // State para submenu
  const [openSubMenu, setOpenSubMenu] = useState<string | null>(null);
  
  // Toggle simples para abrir/fechar dropdowns por nome
  const toggleDropdown = (name: 'file' | 'exportPDF' | 'exportPNG' | null) => {
    setOpenDropdown((prev) => (prev === name ? null : name));
    if (name === null) setOpenSubMenu(null);
  };
  
  // Toggle para submenus
  const toggleSubMenu = (name: string | null) => {
    setOpenSubMenu((prev) => (prev === name ? null : name));
  };

  const handleNavigateToList = () => {
    navigate('/acoes');
    if (location.pathname === '/acoes' || location.pathname === '/acoes/lista') {
      onViewModeChange('list');
    }
  };

  const handleNavigateToKanban = () => {
    navigate('/acoes/kanban');
    if (location.pathname === '/acoes/kanban') {
      onViewModeChange('kanban');
    }
  };

  const handleNavigateToDashboard = () => {
    navigate('/acoes/dashboard');
    if (location.pathname === '/acoes/dashboard') {
      onIndicators?.();
    }
  };

  const handleNavigateToMatrix = () => {
    navigate('/acoes/matriz');
    if (location.pathname === '/acoes/matriz') {
      onViewModeChange('matrix');
    }
  };

  const handleNavigateToNewAction = () => {
    navigate('/acoes/nova');
    onNewAction();
  };

  const handleOpenNewMap = () => {
    if (onNewMap) {
      onNewMap();
      return;
    }
    navigate('/mapa');
  };
  
  // Handler para hover - abre submenu automaticamente
  const handleMenuItemHover = (name: string) => {
    if (openDropdown === 'file') {
      setOpenSubMenu(name);
    }
  };
  
  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (openDropdown === 'projectScope' && !target.closest('[data-project-scope-dropdown]')) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDropdown]);
  
  // Estados para posicionamento dos dropdowns
  const [filePosition, setFilePosition] = useState({ top: 0, left: 0 });
  const [filterPosition, setFilterPosition] = useState({ top: 0, left: 0 });
  const [exportPDFPosition, setExportPDFPosition] = useState({ top: 0, left: 0 });
  const [exportPNGPosition, setExportPNGPosition] = useState({ top: 0, left: 0 });

  // Calcular posições dos dropdowns
  useEffect(() => {
    // Throttle para otimizar performance durante scroll
    let scrollTimeout: NodeJS.Timeout | null = null;
    let resizeTimeout: NodeJS.Timeout | null = null;
    
    const updatePositions = () => {
      if (openDropdown === 'file' && fileButtonRef.current) {
        const rect = fileButtonRef.current.getBoundingClientRect();
        setFilePosition({ top: rect.bottom, left: rect.left });
      }
      if (openDropdown === 'filter' && filterButtonRef.current) {
        const rect = filterButtonRef.current.getBoundingClientRect();
        setFilterPosition({ top: rect.bottom, left: rect.left });
      }
      if (openSubMenu === 'exportPDF' && exportPDFButtonRef.current) {
        const rect = exportPDFButtonRef.current.getBoundingClientRect();
        setExportPDFPosition({ top: rect.top, left: rect.right + 5 });
      }
      if (openSubMenu === 'exportPNG' && exportPNGButtonRef.current) {
        const rect = exportPNGButtonRef.current.getBoundingClientRect();
        setExportPNGPosition({ top: rect.top, left: rect.right + 5 });
      }
    };
    
    // Handler otimizado com throttle para scroll
    const handleScroll = () => {
      if (scrollTimeout) return; // Ignora se já está processando
      scrollTimeout = setTimeout(() => {
        updatePositions();
        scrollTimeout = null;
      }, 16); // ~60fps
    };
    
    // Handler otimizado com debounce para resize
    const handleResize = () => {
      if (resizeTimeout) clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(updatePositions, 100);
    };
    
    const timeoutId = setTimeout(updatePositions, 0);
    
    // Só adiciona listeners se o dropdown estiver aberto
    if (openDropdown || openSubMenu) {
      window.addEventListener('scroll', handleScroll, { passive: true, capture: true });
      window.addEventListener('resize', handleResize);
    }
    
    return () => {
      clearTimeout(timeoutId);
      if (scrollTimeout) clearTimeout(scrollTimeout);
      if (resizeTimeout) clearTimeout(resizeTimeout);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [openDropdown, openSubMenu]);

  if (mode === 'action_plan') {
    return (
      <div 
        data-ribbon="true" 
        className="w-full glass-action-ribbon officebar-surface relative shrink-0 select-none text-[var(--ribbon-text-primary)]"
        ref={ribbonRef} 
        style={{ zIndex: 20, height: '40px', background: 'var(--officebar-bg)', backgroundColor: 'var(--officebar-bg)', backgroundImage: 'none' }}
      >
        <div className="h-full flex items-center px-2 max-w-[1920px] mx-auto">
          {/* === ZONA DE FERRAMENTAS === */}
          <div className="flex items-center gap-0.5 ml-2">
            {/* Grupo 1: Início/Arquivo/Salvar */}
            {goHome && (
              <ToolBtn 
                icon={<Home size={18} strokeWidth={1.5} />}
                label="Início"
                onClick={goHome}
                title="Voltar ao Início"
              />
            )}
            
            <div 
              className="relative h-full" 
              ref={fileButtonRef}
              onMouseEnter={() => setOpenDropdown('file')}
            >
              <ToolBtn 
                icon={<FolderOpen size={18} strokeWidth={1.5} />}
                label="Arquivo"
                onClick={() => toggleDropdown('file')}
                title="Menu Arquivo"
                hasDropdown
              />
                {openDropdown === 'file' && createPortal(
                  <div
                    onMouseLeave={() => { setOpenDropdown(null); setOpenSubMenu(null); }}
                    className="fixed bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded shadow-xl dark:shadow-2xl p-2 z-[10000]"
                    style={{
                      position: 'fixed',
                      top: `${filePosition.top + 8}px`,
                      left: `${filePosition.left}px`,
                      width: '200px',
                    }}
                  >
                    <button 
                      onClick={() => { handleOpenNewMap(); setOpenDropdown(null); }}
                      onMouseEnter={() => setOpenSubMenu(null)}
                      className="w-full text-left px-3 py-2 text-xs hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded flex items-center gap-3 text-blue-600 dark:text-blue-400 font-medium"
                    >
                      <Plus size={14} className="text-blue-500"/> 
                      Novo Mapa
                    </button>
                    
                    <div className="h-px bg-slate-100 dark:bg-slate-600 my-2" />
                    
                    <div className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase px-3 py-1">Projeto</div>
                    <button 
                      onClick={() => { onProjectSettings?.(); setOpenDropdown(null); }}
                      onMouseEnter={() => setOpenSubMenu(null)}
                      className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 dark:hover:bg-slate-700 rounded flex items-center gap-3 text-slate-700 dark:text-slate-200"
                    >
                      <Settings2 size={14} className="text-slate-500 dark:text-slate-400"/> 
                      Projeto
                    </button>
                    
                    <div className="h-px bg-slate-100 dark:bg-slate-600 my-2" />
                    
                    <button 
                      onClick={() => { onSaveLocal?.(); setOpenDropdown(null); }}
                      onMouseEnter={() => setOpenSubMenu(null)}
                      className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 dark:hover:bg-slate-700 rounded flex items-center gap-3 text-slate-700 dark:text-slate-200"
                    >
                      <Save size={14} className="text-slate-500 dark:text-slate-400"/> 
                      Salvar Local
                    </button>
                    
                    <button 
                      onClick={() => { onOpenLocal?.(); setOpenDropdown(null); }}
                      onMouseEnter={() => setOpenSubMenu(null)}
                      className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 dark:hover:bg-slate-700 rounded flex items-center gap-3 text-slate-700 dark:text-slate-200"
                    >
                      <FolderOpen size={14} className="text-slate-500 dark:text-slate-400"/> 
                      Abrir Local
                    </button>
                    
                    <div className="h-px bg-slate-100 dark:bg-slate-600 my-2" />
                    
                    <button 
                      onClick={() => { onImportExcel?.(); setOpenDropdown(null); }}
                      onMouseEnter={() => setOpenSubMenu(null)}
                      className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 dark:hover:bg-slate-700 rounded flex items-center gap-3 text-slate-700 dark:text-slate-200"
                    >
                      <FileUp size={14} className="text-slate-500 dark:text-slate-400"/> 
                      Importar Excel
                    </button>
                    
                    <button 
                      onClick={() => { onDownloadTemplate?.(); setOpenDropdown(null); }}
                      onMouseEnter={() => setOpenSubMenu(null)}
                      className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 dark:hover:bg-slate-700 rounded flex items-center gap-3 text-slate-700 dark:text-slate-200"
                    >
                      <FileSpreadsheet size={14} className="text-slate-500 dark:text-slate-400"/> 
                      Baixar Template
                    </button>
                    
                    <button 
                      onClick={() => { onExportExcel?.(); setOpenDropdown(null); }}
                      onMouseEnter={() => setOpenSubMenu(null)}
                      className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 dark:hover:bg-slate-700 rounded flex items-center gap-3 text-slate-700 dark:text-slate-200"
                    >
                      <FileDown size={14} className="text-emerald-600 dark:text-emerald-500"/> 
                      Exportar Ações para Excel
                    </button>
                    
                    <button 
                      onClick={() => { onGenerateFullReport?.(); setOpenDropdown(null); }}
                      onMouseEnter={() => setOpenSubMenu(null)}
                      className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 dark:hover:bg-slate-700 rounded flex items-center gap-3 text-slate-700 dark:text-slate-200"
                    >
                      <FileText size={14} className="text-blue-600 dark:text-blue-500"/> 
                      Relatório Completo de Ações
                    </button>
                    
                    <div className="h-px bg-slate-100 dark:bg-slate-600 my-2" />
                    
                    <button
                      ref={exportPDFButtonRef}
                      onClick={() => toggleSubMenu('exportPDF')}
                      onMouseEnter={() => handleMenuItemHover('exportPDF')}
                      className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 dark:hover:bg-slate-700 rounded flex items-center gap-3 text-slate-700 dark:text-slate-200 justify-between"
                    >
                      <div className="flex items-center gap-3">
                        <FileText size={14} className="text-slate-500 dark:text-slate-400"/> 
                        Exportar PDF
                      </div>
                      <ChevronDown size={12} className="text-slate-400 dark:text-slate-500" />
                    </button>
                    {openSubMenu === 'exportPDF' && createPortal(
                      <div
                        onMouseLeave={() => setOpenSubMenu(null)}
                        className="fixed bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded shadow-xl dark:shadow-2xl p-2 z-[10001]"
                        style={{
                          position: 'fixed',
                          top: `${exportPDFPosition.top}px`,
                          left: `${exportPDFPosition.left}px`,
                          width: '200px',
                        }}
                      >
                        <button 
                          onClick={() => { onExportPDF?.('list'); setOpenDropdown(null); setOpenSubMenu(null); }} 
                          className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 dark:hover:bg-slate-700 rounded flex items-center gap-3 text-slate-700 dark:text-slate-200"
                        >
                          Lista
                        </button>
                        <button 
                          onClick={() => { onExportPDF?.('kanban'); setOpenDropdown(null); setOpenSubMenu(null); }} 
                          className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 dark:hover:bg-slate-700 rounded flex items-center gap-3 text-slate-700 dark:text-slate-200"
                        >
                          Kanban
                        </button>
                        <button 
                          onClick={() => { onExportPDF?.('matrix'); setOpenDropdown(null); setOpenSubMenu(null); }} 
                          className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 dark:hover:bg-slate-700 rounded flex items-center gap-3 text-slate-700 dark:text-slate-200"
                        >
                          Matriz
                        </button>
                        <button 
                          onClick={() => { onExportPDF?.('indicators'); setOpenDropdown(null); setOpenSubMenu(null); }} 
                          className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 dark:hover:bg-slate-700 rounded flex items-center gap-3 text-slate-700 dark:text-slate-200"
                        >
                          Indicadores
                        </button>
                      </div>, document.body
                    )}

                    <button
                      ref={exportPNGButtonRef}
                      onClick={() => toggleSubMenu('exportPNG')}
                      onMouseEnter={() => handleMenuItemHover('exportPNG')}
                      className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 dark:hover:bg-slate-700 rounded flex items-center gap-3 text-slate-700 dark:text-slate-200 justify-between"
                    >
                      <div className="flex items-center gap-3">
                        <Image size={14} className="text-slate-500 dark:text-slate-400"/> 
                        Exportar PNG
                      </div>
                      <ChevronDown size={12} className="text-slate-400 dark:text-slate-500" />
                    </button>
                    {openSubMenu === 'exportPNG' && createPortal(
                      <div
                        onMouseLeave={() => setOpenSubMenu(null)}
                        className="fixed bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded shadow-xl dark:shadow-2xl p-2 z-[10001]"
                        style={{
                          position: 'fixed',
                          top: `${exportPNGPosition.top}px`,
                          left: `${exportPNGPosition.left}px`,
                          width: '200px',
                        }}
                      >
                        <button 
                          onClick={() => { onExportImage?.('list'); setOpenDropdown(null); setOpenSubMenu(null); }} 
                          className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 dark:hover:bg-slate-700 rounded flex items-center gap-3 text-slate-700 dark:text-slate-200"
                        >
                          Lista
                        </button>
                        <button 
                          onClick={() => { onExportImage?.('kanban'); setOpenDropdown(null); setOpenSubMenu(null); }} 
                          className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 dark:hover:bg-slate-700 rounded flex items-center gap-3 text-slate-700 dark:text-slate-200"
                        >
                          Kanban
                        </button>
                        <button 
                          onClick={() => { onExportImage?.('matrix'); setOpenDropdown(null); setOpenSubMenu(null); }} 
                          className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 dark:hover:bg-slate-700 rounded flex items-center gap-3 text-slate-700 dark:text-slate-200"
                        >
                          Matriz
                        </button>
                        <button 
                          onClick={() => { onExportImage?.('indicators'); setOpenDropdown(null); setOpenSubMenu(null); }} 
                          className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 dark:hover:bg-slate-700 rounded flex items-center gap-3 text-slate-700 dark:text-slate-200"
                        >
                          Indicadores
                        </button>
                      </div>, document.body
                    )}
                  </div>, document.body
                )}
              </div>

              <ToolBtn
                icon={<Save size={18} strokeWidth={1.5} />}
                label="Salvar"
                onClick={onSave}
                title="Salvar Alterações"
              />
              
              <ToolBtn
                icon={<RotateCcw size={18} strokeWidth={1.5} />}
                label="Desfazer"
                onClick={onUndo}
                title="Desfazer (Ctrl+Z)"
                disabled={!onUndo}
              />
              
              <ToolBtn
                icon={<RotateCw size={18} strokeWidth={1.5} />}
                label="Refazer"
                onClick={onRedo}
                title="Refazer (Ctrl+Y)"
                disabled={!onRedo}
              />

            <Separator />

            {/* Grupo 2: Lista/Kanban/Matriz */}
            <ToolBtn
              icon={<List size={18} strokeWidth={1.5} />}
              label="Lista"
              active={viewMode === 'list' && !indicatorsOpen}
              onClick={handleNavigateToList}
              title="Visualização em Lista"
            />
            <ToolBtn
              icon={<Grid3X3 size={16} strokeWidth={1.5} />}
              label="Kanban"
              active={viewMode === 'kanban' && !indicatorsOpen}
              onClick={handleNavigateToKanban}
              title="Quadro Kanban"
            />
            <ToolBtn
              icon={<BarChart3 size={18} strokeWidth={1.5} />}
              label="Matriz"
              active={viewMode === 'matrix' && !indicatorsOpen}
              onClick={handleNavigateToMatrix}
              title="Matriz de Priorização"
            />

            <Separator />

            {/* Grupo 3: Dashboard */}
            <ToolBtn 
              icon={<BarChart2 size={18} strokeWidth={1.5} />}
              label="Dashboard"
              onClick={handleNavigateToDashboard}
              active={indicatorsOpen}
              title="Dashboard de Indicadores"
            />

            <Separator />

            {/* Nova Ação */}
            <ToolBtn 
              icon={<Plus size={18} strokeWidth={2} />}
              label="Nova Ação"
              onClick={handleNavigateToNewAction}
              title="Criar Nova Ação"
            />
          </div>

          {/* DIREITA: Filtros */}
          <div className="flex items-center h-full gap-2 ml-auto">
            <select 
              value={actionScope === 'specific' ? `specific:${specificProjectId}` : actionScope}
              onChange={(e) => {
                const value = e.target.value;
                if (value.startsWith('specific:')) {
                  const projectId = value.replace('specific:', '');
                  onActionScopeChange?.('specific');
                  onSpecificProjectChange?.(projectId);
                } else {
                  onActionScopeChange?.(value as 'current' | 'all');
                }
              }}
              className="h-8 w-[160px] px-2 border border-slate-200 dark:border-slate-600 rounded text-xs bg-white dark:bg-slate-800 text-gray-700 dark:text-slate-200 hover:border-slate-300 dark:hover:border-slate-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors outline-none"
            >
              <option value="current">Projeto: Atual</option>
              <option value="all">Projeto: Todos</option>
              {projects.length > 0 && (
                <optgroup label="Projeto Específico">
                  {projects.map((project) => (
                    <option key={project.id} value={`specific:${project.id}`}>
                      {project.nome_projeto}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>

            <select 
              value={selectedStatus}
              onChange={(e) => onStatusChange?.(e.target.value)}
              className="h-8 w-[160px] px-2 border border-slate-200 dark:border-slate-600 rounded text-xs bg-white dark:bg-slate-800 text-gray-700 dark:text-slate-200 hover:border-slate-300 dark:hover:border-slate-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors outline-none"
            >
              <option value="all">Status: Todos</option>
              <option value="todo">A Fazer</option>
              <option value="doing">Em Andamento</option>
              <option value="done">Concluído</option>
              <option value="overdue">Atrasados</option>
            </select>

            <select 
              value={selectedSector}
              onChange={(e) => onSectorChange?.(e.target.value)}
              className="h-8 w-[160px] px-2 border border-slate-200 dark:border-slate-600 rounded text-xs bg-white dark:bg-slate-800 text-gray-700 dark:text-slate-200 hover:border-slate-300 dark:hover:border-slate-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors outline-none"
            >
              <option value="all">Departamento: Todos</option>
              {sectors.map((sector) => (
                <option key={sector} value={sector}>{sector}</option>
              ))}
            </select>

            <select 
              value={selectedResponsible}
              onChange={(e) => onResponsibleChange?.(e.target.value)}
              className="h-8 w-[160px] px-2 border border-slate-200 dark:border-slate-600 rounded text-xs bg-white dark:bg-slate-800 text-gray-700 dark:text-slate-200 hover:border-slate-300 dark:hover:border-slate-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors outline-none"
            >
              <option value="all">Responsável: Todos</option>
              {responsibles.map((resp) => (
                <option key={resp} value={resp}>{resp}</option>
              ))}
            </select>
          </div>
        </div>
      </div>
    );
  }

  // Default / designer toolbar (fallback)
  return (
    <div data-ribbon="true" className="w-full glass-action-ribbon officebar-surface relative shrink-0 select-none flex justify-between items-center h-[40px] px-3 text-[var(--ribbon-text-primary)]" ref={ribbonRef} style={{ zIndex: 20, background: 'var(--officebar-bg)', backgroundColor: 'var(--officebar-bg)', backgroundImage: 'none' }}>
      {/* Esquerda: arquivo + undo/redo agrupados */}
      <div className="flex items-center gap-2">
        <ButtonGroup>
          {goHome && (
            <ToolBtn icon={<Home size={20} strokeWidth={1.5} />} onClick={goHome} title="Início" />
          )}
          <div className="relative" ref={fileButtonRef}>
            <ToolBtn icon={<FolderOpen size={20} strokeWidth={1.5} />} onClick={() => toggleDropdown('file')} title="Arquivo" />
            {openDropdown === 'file' && createPortal(
              <div className="fixed bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded shadow-xl dark:shadow-2xl p-2 z-[10000]" style={{ position: 'fixed', top: `${filePosition.top + 8}px`, left: `${filePosition.left}px`, width: '256px' }}>
                <div className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase px-3 py-1">Projeto</div>
                <button onClick={() => { onSave?.(); setOpenDropdown(null); }} className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 dark:hover:bg-slate-700 rounded flex items-center gap-3 text-slate-700 dark:text-slate-200"><Save size={14} className="text-slate-500 dark:text-slate-400"/> Salvar Alterações</button>
                <button onClick={() => { onProjectSettings?.(); setOpenDropdown(null); }} className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 dark:hover:bg-slate-700 rounded flex items-center gap-3 text-slate-700 dark:text-slate-200"><Settings2 size={14} className="text-slate-500 dark:text-slate-400"/> Configurações</button>
                <div className="h-px bg-slate-100 dark:bg-slate-600 my-2" />
                <div className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase px-3 py-1">Exportar</div>
                <button onClick={() => { onExportExcel?.('list'); setOpenDropdown(null); }} className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 dark:hover:bg-slate-700 rounded flex items-center gap-3 text-slate-700 dark:text-slate-200"><FileSpreadsheet size={14} className="text-slate-500 dark:text-slate-400"/> Exportar Excel</button>
                <button onClick={() => { onExportPDF?.('list'); setOpenDropdown(null); }} className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 dark:hover:bg-slate-700 rounded flex items-center gap-3 text-slate-700 dark:text-slate-200"><FileText size={14} className="text-slate-500 dark:text-slate-400"/> Exportar PDF (Lista)</button>
                <button onClick={() => { onExportPDF?.('kanban'); setOpenDropdown(null); }} className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 dark:hover:bg-slate-700 rounded flex items-center gap-3 text-slate-700 dark:text-slate-200"><FileText size={14} className="text-slate-500 dark:text-slate-400"/> Exportar PDF (Kanban)</button>
                <button onClick={() => { onExportPDF?.('matrix'); setOpenDropdown(null); }} className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 dark:hover:bg-slate-700 rounded flex items-center gap-3 text-slate-700 dark:text-slate-200"><FileText size={14} className="text-slate-500 dark:text-slate-400"/> Exportar PDF (Matriz)</button>
                <button onClick={() => { onExportImage?.('list'); setOpenDropdown(null); }} className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 dark:hover:bg-slate-700 rounded flex items-center gap-3 text-slate-700 dark:text-slate-200"><FileText size={14} className="text-slate-500 dark:text-slate-400"/> Exportar PNG (Lista)</button>
                <button onClick={() => { onExportImage?.('kanban'); setOpenDropdown(null); }} className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 dark:hover:bg-slate-700 rounded flex items-center gap-3 text-slate-700 dark:text-slate-200"><FileText size={14} className="text-slate-500 dark:text-slate-400"/> Exportar PNG (Kanban)</button>
                <button onClick={() => { onExportImage?.('matrix'); setOpenDropdown(null); }} className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 dark:hover:bg-slate-700 rounded flex items-center gap-3 text-slate-700 dark:text-slate-200"><FileText size={14} className="text-slate-500 dark:text-slate-400"/> Exportar PNG (Matriz)</button>
              </div>, document.body
            )}
          </div>

          <Separator />

          <ToolBtn icon={<RotateCcw size={20} strokeWidth={1.5} />} onClick={onUndo} title="Desfazer" />
          <ToolBtn icon={<RotateCw size={20} strokeWidth={1.5} />} onClick={onRedo} title="Refazer" />
        </ButtonGroup>
      </div>

      {/* Segmented Control removido daqui - agora localizado no topo (AppHeader) */}

      {/* Direita: Ações globais e botão Ação (Hero) */}
      <div className="flex items-center gap-2">
        <ButtonGroup>
          <ToolBtn icon={<Save size={20} strokeWidth={1.5} />} onClick={onSave} title="Salvar" />
          <ToolBtn icon={<Users size={20} strokeWidth={1.5} />} onClick={onManageTeam} title="Equipe" />
        </ButtonGroup>

        <div>
          <button onClick={handleNavigateToNewAction} className="flex items-center gap-1.5 px-3 h-8 bg-blue-600 text-white rounded-sm text-xs font-semibold hover:bg-blue-700 transition whitespace-nowrap border border-blue-500/30">
            <Plus size={15} strokeWidth={1.5} />
            Ação
          </button>
        </div>
      </div>
    </div>
  );
};

export default ActionRibbon;
